import { Footprints, Train, GraduationCap, Shield } from 'lucide-react';
import { MetricCard, SectionTitle } from '@/components/Luxury';
import { useNeighborhoodScores } from '@/hooks/useNeighborhoodScores';

interface MarketIntelligenceProps {
  propertyId: string;
}

const getWalkScoreColor = (score: number | null): string => {
  if (score == null) return 'text-slate-400';
  if (score >= 90) return 'text-emerald-400';
  if (score >= 70) return 'text-emerald-300';
  if (score >= 50) return 'text-amber-400';
  if (score >= 25) return 'text-orange-400';
  return 'text-red-400';
};

const getScoreVariant = (score: number | null, goodThreshold: number): 'default' | 'green' => {
  if (score != null && score > goodThreshold) return 'green';
  return 'default';
};

const isPoor = (score: number | null, poorThreshold: number): boolean => {
  return score != null && score < poorThreshold;
};

const formatTrend = (trend: string): string => {
  return trend.charAt(0).toUpperCase() + trend.slice(1);
};

const sectionIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
    />
  </svg>
);

const crimsonBorderClass = 'border-2 border-red-500';

export function MarketIntelligence({ propertyId }: MarketIntelligenceProps) {
  const { data, isLoading, isError } = useNeighborhoodScores(propertyId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SectionTitle icon={sectionIcon}>Market Intelligence</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"
            >
              <div className="h-3 w-24 bg-slate-700 rounded animate-pulse mb-3" />
              <div className="h-8 w-20 bg-slate-700 rounded animate-pulse mb-2" />
              <div className="h-3 w-32 bg-slate-700/60 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    // Hide entirely when no neighborhood data available (Issue 11)
    return null;
  }

  // Hide when ALL scores are null/unavailable — no point showing empty cards
  const hasAnyData = data.walk_score != null || data.transit_score != null
    || data.school_score != null || data.safety_index != null;
  if (!hasAnyData) {
    return null;
  }

  return (
    <div className="space-y-6">
      <SectionTitle icon={sectionIcon}>Market Intelligence</SectionTitle>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Walk Score Card — only when data available */}
        {data.walk_score != null && (
          <div className="relative">
            <div className="absolute top-3 right-3 text-stone-700 z-10">
              <Footprints className="w-5 h-5" />
            </div>
            <MetricCard
              label="Walk Score"
              value={`${data.walk_score}/100`}
              hint={data.walk_description || undefined}
              variant={getScoreVariant(data.walk_score, 70)}
              className={`${isPoor(data.walk_score, 40) ? crimsonBorderClass : ''} ${getWalkScoreColor(data.walk_score)}`}
            />
          </div>
        )}

        {/* Transit Score Card — only when data available */}
        {data.transit_score != null && (
          <div className="relative">
            <div className="absolute top-3 right-3 text-stone-700 z-10">
              <Train className="w-5 h-5" />
            </div>
            <MetricCard
              label="Transit Score"
              value={`${data.transit_score}/100`}
              hint={data.bike_score != null ? `Bike Score: ${data.bike_score}/100` : undefined}
              variant={getScoreVariant(data.transit_score, 70)}
              className={isPoor(data.transit_score, 40) ? crimsonBorderClass : ''}
            />
          </div>
        )}

        {/* Schools Card — only when data available */}
        {data.school_score != null && (
          <div className="relative">
            <div className="absolute top-3 right-3 text-stone-700 z-10">
              <GraduationCap className="w-5 h-5" />
            </div>
            <MetricCard
              label="Schools"
              value={`${data.school_score}/10`}
              hint={
                data.school_count != null
                  ? `${data.school_count} school${data.school_count !== 1 ? 's' : ''} nearby${data.school_grade ? ` (${data.school_grade})` : ''}`
                  : data.school_grade
                    ? `Grade: ${data.school_grade}`
                    : undefined
              }
              variant={getScoreVariant(data.school_score, 7)}
              className={isPoor(data.school_score, 4) ? crimsonBorderClass : ''}
            />
          </div>
        )}

        {/* Safety Index Card — only when data available */}
        {data.safety_index != null && (
          <div className="relative">
            <div className="absolute top-3 right-3 text-stone-700 z-10">
              <Shield className="w-5 h-5" />
            </div>
            <MetricCard
              label="Safety Index"
              value={`${data.safety_index}/100`}
              hint={
                data.crime_trend
                  ? `Trend: ${formatTrend(data.crime_trend)}${data.crime_grade ? ` | Grade: ${data.crime_grade}` : ''}`
                  : data.crime_grade
                    ? `Grade: ${data.crime_grade}`
                    : undefined
              }
              variant={getScoreVariant(data.safety_index, 70)}
              className={isPoor(data.safety_index, 40) ? crimsonBorderClass : ''}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MarketIntelligence;
